import { z } from 'zod'
import { VideoIdSchema } from '../video'
import { QuizIdSchema } from '../quiz'
import { AccountStatusIdSchema, UserIdSchema, WorkflowActionIdSchema, WorkflowStageIdSchema } from '../ids'
import { DispositionIdSchema } from '../disposition'

export const ProgramActivityIdSchema = z
  .union([AccountStatusIdSchema, DispositionIdSchema, z.literal('contact'), z.literal('pitch')])
  .meta({
    description:
      'Activity that counts toward a task: an account status ID (`AS.`), a disposition ID (`Disposition.` or `Disposition:`), `contact`, or `pitch`',
    example: 'pitch',
  })
export type ProgramActivityId = z.infer<typeof ProgramActivityIdSchema>

export const PROGRAM_TASK_CRITERIA_TYPES = [
  'activity',
  'workflow',
  'video',
  'videoChat',
  'shadow',
  'evaluation',
  'convo',
  'externalLink',
  'quiz',
] as const
export const ProgramTaskCriteriaTypeSchema = z.enum(PROGRAM_TASK_CRITERIA_TYPES)
export type ProgramTaskCriteriaType = z.infer<typeof ProgramTaskCriteriaTypeSchema>

const ProgramTaskActivitySchema = z.object({
  type: z.literal('activity'),
  activityId: ProgramActivityIdSchema,
  count: z.number().int().positive(),
})
export type ProgramTaskActivity = z.infer<typeof ProgramTaskActivitySchema>

const ProgramTaskWorkflowActivitySchema = z.object({
  type: z.literal('workflow'),
  count: z.number().int().positive(),
  stageOrActionId: z.union([WorkflowStageIdSchema, WorkflowActionIdSchema]),
})
export type ProgramTaskWorkflowActivity = z.infer<typeof ProgramTaskWorkflowActivitySchema>

const ProgramTaskVideoSchema = z.object({
  type: z.literal('video'),
  videoId: VideoIdSchema.optional(),
  tags: z.array(z.string()).optional(),
  description: z.string().optional(),
})
export type ProgramTaskVideo = z.infer<typeof ProgramTaskVideoSchema>

const ProgramVideoChatTargetSchema = z
  .union([z.literal('manager'), UserIdSchema])
  .describe('The target for the video chat (any manager or a specific user), manager by default if not specified')
const ProgramTaskVideoChatSchema = z.object({
  type: z.literal('videoChat'),
  description: z.string().optional(),
  target: ProgramVideoChatTargetSchema.optional(),
  createResponseTask: z
    .boolean()
    .optional()
    .describe('Whether to create a task for the user to respond to the message'),
})
export type ProgramTaskVideoChat = z.infer<typeof ProgramTaskVideoChatSchema>

export const SHADOW_TARGETS = ['manager', 'team'] as const
export type ShadowTarget = (typeof SHADOW_TARGETS)[number]
export const EVENT_COMPLETE_WHEN = ['scheduled', 'happened'] as const
export type EventCompleteWhen = (typeof EVENT_COMPLETE_WHEN)[number]

const ProgramTaskShadowSchema = z.object({
  type: z.literal('shadow'),
  target: z.enum(SHADOW_TARGETS),
  completeWhen: z.enum(EVENT_COMPLETE_WHEN),
  count: z.number().int().positive().optional(),
})
export type ProgramTaskShadow = z.infer<typeof ProgramTaskShadowSchema>

const ProgramTaskEvaluatorSchema = z.enum(['manager', 'self'])
export type ProgramTaskEvaluator = z.infer<typeof ProgramTaskEvaluatorSchema>
export const EVAL_COMPLETE_WHEN = ['submitted', 'graded', 'selfAssessed'] as const
export type EvalCompleteWhen = (typeof EVAL_COMPLETE_WHEN)[number]

const ProgramTaskEvaluationSchema = z.object({
  type: z.literal('evaluation'),
  /** @deprecated */
  evaluator: ProgramTaskEvaluatorSchema.optional(),
  completeWhen: z
    .enum(EVAL_COMPLETE_WHEN)
    .optional()
    .describe('When the evaluation should be marked as complete, default to graded'),
  count: z.number().int().positive().optional(),
})
export type ProgramTaskEvaluation = z.infer<typeof ProgramTaskEvaluationSchema>

const CONVO_COMPLETE_WHEN = [
  'recorded',
  'reviewedFeedback',
  'selfAssessed',
  'submittedForFeedback',
  'managerFeedback',
] as const
export const ConvoCompleteWhenSchema = z.enum(CONVO_COMPLETE_WHEN)
export type ConvoCompleteWhen = z.infer<typeof ConvoCompleteWhenSchema>
const ProgramTaskConvoSchema = z.object({
  type: z.literal('convo'),
  /** @defaultValue recorded */
  completeWhen: ConvoCompleteWhenSchema.optional(),
  count: z.number().int().positive().optional(),
})
export type ProgramTaskConvo = z.infer<typeof ProgramTaskConvoSchema>

const EXTERNAL_LINK_COMPLETE_WHEN = ['clicked', 'external'] as const
export const ExternalLinkCompleteWhenSchema = z.enum(EXTERNAL_LINK_COMPLETE_WHEN)
export type ExternalLinkCompleteWhen = z.infer<typeof ExternalLinkCompleteWhenSchema>
export const TaskExternalLinkSchema = z.object({
  type: z.literal('externalLink'),
  label: z.string().optional(),
  url: z.string(),
  completeWhen: ExternalLinkCompleteWhenSchema,
  count: z.number().int().optional(),
})
export type ProgramTaskExternalLink = z.infer<typeof TaskExternalLinkSchema>

const TaskQuizSchema = z.object({
  type: z.literal('quiz'),
  quizId: QuizIdSchema,
})
export type ProgramTaskQuiz = z.infer<typeof TaskQuizSchema>

export const ProgramTaskCriteriaSchema = z.discriminatedUnion('type', [
  ProgramTaskActivitySchema,
  ProgramTaskWorkflowActivitySchema,
  ProgramTaskVideoSchema,
  ProgramTaskVideoChatSchema,
  ProgramTaskShadowSchema,
  ProgramTaskEvaluationSchema,
  ProgramTaskConvoSchema,
  TaskExternalLinkSchema,
  TaskQuizSchema,
])
export type ProgramTaskCriteria = z.infer<typeof ProgramTaskCriteriaSchema>
